import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';

/* Feuilles de style : toutes chargées d'emblée, dans leur ordre d'origine.
   Les pages se partagent des règles globales (variables, boutons, titres) :
   les découper avec le code changerait l'apparence selon le chemin suivi par
   le visiteur. Ensemble, elles ne pèsent que 25 Ko une fois compressées.
   Toute nouvelle feuille de style (page, composant ou bibliothèque) est à ajouter
   ici, à la place qu'elle occupe dans l'ordre des imports. */
import './components/LogoAnimation.css';
import './components/Partenaires.css';
import './pages/PagesLegales.css';
import './components/SelecteurLangue.css';
import './pages/Home.css';
import './pages/Devis.css';
import './components/CodeGate.css';
import './components/VisionneusePhoto.css';
import 'react-datepicker/dist/react-datepicker.css';
import './pages/Commande.css';
import './pages/AdminMenu.css';
import './pages/Login.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './components/CalendrierDevis.css';
import './components/Modale.css';
import './pages/GererDevis.css';
import './pages/GererPhotos.css';
import './pages/Galerie.css';
import './pages/ApercuDevis.css';
import './pages/CodeAdmin.css';
import './pages/MenusCarte.css';
import './pages/Formules.css';
import './pages/Analytics.css';
import './pages/Maquette.css';
import './pages/PlanClient.css';

import Home from './pages/Home.jsx';
import { LangueProvider, useLangue } from './i18n/Langue';

/* ─────────────────────────────────────────────────────────────
   Chargement des pages à la demande.
   Seul l'accueil est dans le programme principal : chaque autre page
   est téléchargée quand on y va. Un visiteur de l'accueil ne reçoit
   donc plus le générateur de PDF, le calendrier ni les pages admin.

   Après une nouvelle mise en ligne, un onglet resté ouvert pointe vers
   des morceaux qui n'existent plus : on recharge alors la page une
   fois, pour repartir sur la version à jour.
───────────────────────────────────────────────────────────── */
const CLE_RECHARGEMENT = 'rechargement-version';

const page = (charger) => lazy(() =>
  charger()
    .then((module) => { sessionStorage.removeItem(CLE_RECHARGEMENT); return module; })
    .catch((err) => {
      if (sessionStorage.getItem(CLE_RECHARGEMENT)) throw err;
      sessionStorage.setItem(CLE_RECHARGEMENT, '1');
      window.location.reload();
      return new Promise(() => {}); // la page se recharge, rien à rendre
    })
);

const Devis           = page(() => import('./pages/Devis.jsx'));
const Commande        = page(() => import('./pages/Commande.jsx'));
const AdminMenu       = page(() => import('./pages/AdminMenu.jsx'));
const Login           = page(() => import('./pages/Login'));
const GererDevis      = page(() => import('./pages/GererDevis'));
const GererPhotos     = page(() => import('./pages/GererPhotos'));
const Galerie         = page(() => import('./pages/Galerie'));
const ApercuDevis     = page(() => import('./pages/ApercuDevis.jsx'));
const CodeAdmin       = page(() => import('./pages/CodeAdmin.jsx'));
const MenusCarte      = page(() => import('./pages/MenusCarte.jsx'));
const Formules        = page(() => import('./pages/Formules.jsx'));
const Traiteur        = page(() => import('./pages/Traiteur.jsx'));
const MentionsLegales = page(() => import('./pages/MentionsLegales.jsx'));
const Confidentialite = page(() => import('./pages/Confidentialite.jsx'));
const Analytics       = page(() => import('./pages/Analytics.jsx'));
const Contact         = page(() => import('./pages/Contact.jsx'));
const Maquette        = page(() => import('./pages/Maquette.jsx'));
const PlanClient      = page(() => import('./pages/PlanClient.jsx'));

/* Affiché le temps de télécharger une page */
const Attente = () => {
  const { langue } = useLangue();
  return (
  <div style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: '#FAFAF8', color: '#666', fontFamily: "'Nunito', system-ui, sans-serif", fontSize: 15,
  }}>
    {langue === 'en' ? 'Loading…' : 'Chargement…'}
  </div>
  );
};

function App() {
  return (
    <LangueProvider>
    <Router>
      <Suspense fallback={<Attente />}>
        <Routes>
          <Route path="/"             element={<Home />} />
          <Route path="/devis"        element={<Devis />} />
          <Route path="/apercu-devis" element={<ApercuDevis />} />
          <Route path="/commande"     element={<Commande />} />
          <Route path="/admin-menu"   element={<AdminMenu />} />
          <Route path="/login"        element={<Login />} />
          <Route path="/gerer-devis"  element={<GererDevis />} />
          <Route path="/gerer-photos" element={<GererPhotos />} />
          <Route path="/galerie"      element={<Galerie />} />
          <Route path="/code-admin"   element={<CodeAdmin />} />
          <Route path="/menus-carte"  element={<MenusCarte />} />
          <Route path="/formules"     element={<Formules />} />
          <Route path="/traiteur"     element={<Traiteur />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite"  element={<Confidentialite />} />
          <Route path="/analytics"        element={<Analytics />} />
          <Route path="/contact"          element={<Contact />} />
          <Route path="/maquette"         element={<Maquette />} />
          <Route path="/plan/:id/:cle"    element={<PlanClient />} />
        </Routes>
      </Suspense>
    </Router>
    </LangueProvider>
  );
}

export default App;
